import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Exportar reportes a PDF
export const exportToPDF = (data, config) => {
  const {
    title = 'Reporte',
    headers = [],
    rows = [],
    fileName = 'reporte.pdf',
    orientation = 'portrait', // 'portrait' o 'landscape'
    showDate = true,
    summary = null
  } = config;

  const doc = new jsPDF({
    orientation,
    unit: 'mm',
    format: 'letter'
  });

  let yPos = 15;

  // Título
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text(title, 105, yPos, { align: 'center' });
  yPos += 10;

  // Fecha
  if (showDate) {
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Fecha: ${new Date().toLocaleDateString('es-MX')}`, 15, yPos);
    yPos += 7;
  }

  // Resumen (si existe)
  if (summary) {
    yPos += 5;
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Resumen', 15, yPos);
    yPos += 7;
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    Object.entries(summary).forEach(([key, value]) => {
      doc.text(`${key}: ${value}`, 15, yPos);
      yPos += 5;
    });
    yPos += 5;
  }

  // Tabla
  doc.autoTable({
    startY: yPos,
    head: [headers],
    body: rows,
    theme: 'striped',
    headStyles: {
      fillColor: [37, 99, 235], // primary-600
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 9,
      cellPadding: 2
    },
    columnStyles: {
      // Puedes agregar estilos específicos por columna aquí
    },
    margin: { left: 15, right: 15 }
  });

  // Pie de página
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text(
      `Página ${i} de ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }

  // Guardar
  doc.save(fileName);
};

// Exportar a Excel
export const exportToExcel = (config = {}) => {
  const {
    sheetName = 'Datos',
    fileName = 'reporte.xlsx',
    headers = [],
    rows = []
  } = config;

  // Crear workbook
  const wb = XLSX.utils.book_new();

  // Crear worksheet con headers y rows
  const wsData = headers.length > 0 ? [headers, ...rows] : rows;
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Ajustar ancho de columnas automáticamente
  const colWidths = [];
  wsData.forEach(row => {
    row.forEach((cell, i) => {
      const cellLength = cell ? cell.toString().length : 10;
      colWidths[i] = Math.max(colWidths[i] || 10, cellLength + 2);
    });
  });
  ws['!cols'] = colWidths.map(w => ({ wch: Math.min(w, 50) }));

  // Agregar worksheet al workbook
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Generar archivo y descargar
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  saveAs(blob, fileName);
};

// Exportar inventario completo a Excel
export const exportInventoryToExcel = (productos) => {
  const headers = [
    'SKU',
    'Código de Barras',
    'Nombre',
    'Categoría',
    'Precio Compra',
    'Precio Venta',
    'Stock',
    'Stock Mínimo',
    'Unidad',
    'Activo'
  ];

  const rows = productos.map(p => [
    p.sku || '',
    p.codigo_barras || '',
    p.nombre || '',
    p.categoria_nombre || '',
    p.precio_compra || 0,
    p.precio_venta || 0,
    p.stock_total || 0,
    p.stock_minimo || 0,
    p.unidad || 'pza',
    p.activo ? 'Sí' : 'No'
  ]);

  exportToExcel({
    sheetName: 'Inventario',
    fileName: `inventario_${new Date().toISOString().split('T')[0]}.xlsx`,
    headers,
    rows
  });
};

// Importar inventario desde Excel
export const importInventoryFromExcel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Leer primera hoja
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convertir a JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        // Primera fila son headers, el resto son datos
        const headers = jsonData[0];
        const rows = jsonData.slice(1);
        
        // Mapear a formato de productos
        const productos = rows.map(row => {
          const producto = {};
          headers.forEach((header, index) => {
            const key = header.toLowerCase().replace(/ /g, '_');
            producto[key] = row[index];
          });
          return producto;
        }).filter(p => p.nombre); // Filtrar filas vacías
        
        resolve(productos);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Error al leer archivo'));
    reader.readAsArrayBuffer(file);
  });
};

// Exportar reporte de ventas diario
export const exportDailySalesReport = (ventas, fecha) => {
  const headers = ['Folio', 'Hora', 'Cliente', 'Total', 'Método de Pago', 'Usuario'];
  
  const rows = ventas.map(v => [
    v.folio || v.id,
    new Date(v.fecha_venta).toLocaleTimeString('es-MX'),
    v.cliente_nombre || 'Público General',
    `$${parseFloat(v.total).toFixed(2)}`,
    v.metodo_pago || 'Efectivo',
    v.usuario_nombre || 'Sistema'
  ]);

  const total = ventas.reduce((sum, v) => sum + parseFloat(v.total || 0), 0);
  const cantidad = ventas.length;

  exportToPDF({
    title: `Reporte de Ventas - ${fecha}`,
    headers,
    rows,
    fileName: `ventas_diarias_${fecha}.pdf`,
    summary: {
      'Total de Ventas': cantidad,
      'Monto Total': `$${total.toFixed(2)}`
    }
  });
};

// Exportar reporte mensual
export const exportMonthlySalesReport = (ventas, mes, año) => {
  const headers = ['Fecha', 'Ventas', 'Monto Total', 'Promedio'];
  
  // Agrupar por día
  const ventasPorDia = ventas.reduce((acc, v) => {
    const fecha = new Date(v.fecha_venta).toLocaleDateString('es-MX');
    if (!acc[fecha]) {
      acc[fecha] = { cantidad: 0, total: 0 };
    }
    acc[fecha].cantidad++;
    acc[fecha].total += parseFloat(v.total || 0);
    return acc;
  }, {});

  const rows = Object.entries(ventasPorDia).map(([fecha, data]) => [
    fecha,
    data.cantidad,
    `$${data.total.toFixed(2)}`,
    `$${(data.total / data.cantidad).toFixed(2)}`
  ]);

  const totalVentas = ventas.length;
  const montoTotal = ventas.reduce((sum, v) => sum + parseFloat(v.total || 0), 0);

  exportToPDF({
    title: `Reporte Mensual - ${mes}/${año}`,
    headers,
    rows,
    fileName: `ventas_mensuales_${mes}_${año}.pdf`,
    summary: {
      'Total de Ventas': totalVentas,
      'Monto Total': `$${montoTotal.toFixed(2)}`,
      'Promedio por Venta': `$${(montoTotal / totalVentas).toFixed(2)}`
    }
  });
};

// Exportar a Excel con múltiples hojas
export const exportMultiSheetExcel = (sheets, fileName = 'reporte.xlsx') => {
  const wb = XLSX.utils.book_new();

  sheets.forEach(({ name, headers, rows }) => {
    const wsData = headers ? [headers, ...rows] : rows;
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Ajustar anchos
    const colWidths = [];
    wsData.forEach(row => {
      row.forEach((cell, i) => {
        const cellLength = cell ? cell.toString().length : 10;
        colWidths[i] = Math.max(colWidths[i] || 10, cellLength + 2);
      });
    });
    ws['!cols'] = colWidths.map(w => ({ wch: Math.min(w, 50) }));

    XLSX.utils.book_append_sheet(wb, ws, name);
  });

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  saveAs(blob, fileName);
};

export default {
  exportToPDF,
  exportToExcel,
  exportInventoryToExcel,
  importInventoryFromExcel,
  exportDailySalesReport,
  exportMonthlySalesReport,
  exportMultiSheetExcel
};
